import { TablePage, Dialog, View, MemberList } from 'web-mojo';
import { OperatorList } from '@portal/models/Operator.js';
import '@portal/routes/OperatorView.js';

export default class OperatorsPage extends TablePage {
    constructor(options = {}) {
        super({
            title: 'Operators',
            icon: 'bi-person-badge-fill',
            pageName: 'operators_list',
            route: 'group/operators',
            Collection: OperatorList,
            permissions: ['routes', 'manage_routes', 'manage_group'],
            requiresGroup: true,

            viewDialogOptions: {
                header: false,
                size: 'md',
                footer: false,
            },

            defaultQuery: {
                sort: 'display_name',
                status: 'active',
                group: () => app.activeGroup?.id,
            },

            columns: [
                {
                    label: 'Status',
                    key: 'status',
                    formatter: "badge:active=success,inactive=secondary,on_route=primary",
                    filter: {
                        type: 'select',
                        options: [
                            { label: 'Active', value: 'active' },
                            { label: 'Inactive', value: 'inactive' },
                            { label: 'On Route', value: 'on_route' },
                        ]
                    }
                },
                {
                    label: 'Name',
                    key: 'display_name',
                    formatter: "default('—')",
                },
                {
                    label: 'Username',
                    key: 'user.username',
                    formatter: "default('—')",
                    visibility: 'md',
                },
                {
                    label: 'Email',
                    key: 'user.email',
                    formatter: "default('—')",
                    visibility: 'lg',
                },
                {
                    label: 'Phone',
                    key: 'phone',
                    formatter: "default('—')",
                    visibility: 'md',
                },
            ],

            selectable: true,

            batchActions: [
                { label: 'Deactivate', icon: 'bi bi-x-circle', action: 'deactivate' },
                { label: 'Activate', icon: 'bi bi-check-circle', action: 'activate' },
                { label: 'Export', icon: 'bi bi-download', action: 'export' },
            ],
            searchable: true,
            sortable: true,
            filterable: true,
            paginated: true,

            showRefresh: true,
            showAdd: true,
            showExport: false,

            tableOptions: {
                striped: true,
                bordered: false,
                hover: true,
                responsive: false,
            },

            contextMenu: [
                { icon: 'bi-pencil', label: 'Edit', action: 'edit-operator' },
                { icon: 'bi-x-circle', label: 'Deactivate', action: 'deactivate-operator', className: 'text-danger' },
            ],

            addButtonLabel: 'Add Operator',

            tableViewOptions: {
                hideActivePillNames: ['group'],
                onAdd: () => {
                    const form = new OperatorCreateForm();
                    Dialog.showDialog({
                        title: 'Add Operator',
                        body: form,
                        size: 'md',
                        buttons: [
                            { text: 'Cancel', class: 'btn-secondary', dismiss: true },
                            { text: 'Add Operator', class: 'btn-primary',
                              handler: async () => {
                                  const ok = await form.onActionSave();
                                  if (ok) this.collection.fetch();
                                  return ok;
                              }
                            },
                        ],
                    });
                },
            },

            ...options,
        });
    }

    onActionEditOperator(event, el) {
        this.onActionView(event, el);
    }

    async onActionBatchDeactivate() {
        const selected = this.tableView.getSelectedItems();
        if (!selected?.length) return;
        const confirmed = await Dialog.confirm({
            title: `Deactivate ${selected.length} operator${selected.length > 1 ? 's' : ''}`,
            message: 'Deactivate selected operators? They will be removed from future route assignments.',
            confirmText: 'Deactivate',
            confirmClass: 'btn-danger',
        });
        if (!confirmed) return;
        await Promise.all(selected.map(item => item.model.save({ deactivate: 1 })));
        app.toast.success(`Deactivated ${selected.length} operator${selected.length > 1 ? 's' : ''}`);
        this.tableView.collection.fetch();
    }

    async onActionBatchActivate() {
        const selected = this.tableView.getSelectedItems();
        if (!selected?.length) return;
        await Promise.all(selected.map(item => item.model.save({ activate: 1 })));
        app.toast.success(`Activated ${selected.length} operator${selected.length > 1 ? 's' : ''}`);
        this.tableView.collection.fetch();
    }

    async onActionBatchExport() {
        this.tableView.collection.export();
    }

    async onActionDeactivateOperator(event, el) {
        const id = el.getAttribute('data-id');
        const operator = this.collection.get(id);
        if (!operator) return;

        const confirmed = await Dialog.confirm({
            title: 'Deactivate Operator',
            message: `Deactivate "${operator.nameOrId()}"? They will be removed from future route assignments.`,
            confirmText: 'Deactivate',
            confirmClass: 'btn-danger',
        });
        if (!confirmed) return;

        const response = await operator.save({ deactivate: 1 });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to deactivate operator');
            return;
        }
        const cascade = response.data.data?.cascade;
        const summary = cascade
            ? `${cascade.access_windows_revoked ?? 0} windows revoked, ${cascade.scheduled_routes_flagged ?? 0} routes flagged`
            : 'Operator deactivated';
        app.toast.success(summary);
        this.collection.fetch();
    }
}

class OperatorCreateForm extends View {
    constructor(options = {}) {
        super(options);
        this.members = [];
    }

    async onInit() {
        const members = new MemberList();
        const resp = await members.fetch({
            group: app.activeGroup.id,
            is_active: true,
            size: 200,
            sort: 'user__username',
        });
        if (resp.success && resp.data?.status) {
            this.members = resp.data.data || [];
        }
    }

    async onActionSave() {
        const userId = this.element.querySelector('[name=user_id]').value;
        const phone = this.element.querySelector('[name=phone]').value.trim();

        if (!userId) {
            app.toast.warning('Please select a member');
            return false;
        }

        const response = await app.api.post('/api/routes/operator', {
            user: parseInt(userId),
            group: app.activeGroup.id,
            status: 'active',
            phone: phone || '',
        });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to create operator');
            return false;
        }
        app.toast.success('Operator added');
        this.emit('saved', response.data.data);
        return true;
    }

    set template(v) { this._t = v; }

    get template() {
        const memberOptions = this.members.map(m => {
            const label = m.user?.display_name || m.user?.username || `User ${m.user?.id}`;
            const email = m.user?.email ? ` — ${m.user.email}` : '';
            return `<option value="${m.user?.id}">${label}${email}</option>`;
        }).join('');

        return `
            <div class="p-3">
                <div class="mb-3">
                    <label class="form-label">Member *</label>
                    ${this.members.length === 0
                        ? `<div class="text-muted small">No members found.</div>`
                        : `<select class="form-select" name="user_id">
                               <option value="">Select a member…</option>
                               ${memberOptions}
                           </select>`
                    }
                    <div class="form-text text-muted">Only active group members are shown.</div>
                </div>
                <div class="mb-4">
                    <label class="form-label">Phone</label>
                    <input type="text" class="form-control" name="phone" placeholder="e.g. 512-555-0101">
                </div>
            </div>
        `;
    }
}
